import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag } from '@carbon/react';
import {
  Temperature,
  Wifi,
  BatteryFull,
  Timer,
  Checkmark,
  CheckmarkFilled,
  ArrowRight,
  Certificate,
  WatsonHealthStackedScrolling_1,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';

const SensorsPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('sensors');

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const sensors = [
    {
      name: 'KBeacon',
      category: 'BLE Temperature Beacon',
      image: '/assets/1-Black-KBeacon.webp',
      description: 'Affordable Bluetooth Low Energy beacon with integrated temperature sensor. Perfect for cost-effective deployments requiring reliable temperature monitoring with offline data logging capabilities.',
      detailedDescription: 'KBeacon sensors are compact, battery-powered devices that continuously monitor temperature and store readings locally. They automatically sync data when connected to a gateway or mobile device, making them ideal for warehouses, cold storage facilities, and transportation monitoring. The sensors feature long battery life and can operate independently for extended periods.',
      specs: [
        { label: 'Temperature Range', value: '-20°C to 60°C' },
        { label: 'Accuracy', value: '±0.5°C' },
        { label: 'Battery Life', value: '3 years' },
        { label: 'Connectivity', value: 'BLE 5.0' },
        { label: 'Data Storage', value: 'Offline logging' },
        { label: 'Size', value: 'Compact (coin cell)' },
      ],
      features: ['Budget-friendly pricing', 'Easy deployment', 'Offline data logging', 'Bulk data sync', 'Long battery life', 'Real-time alerts'],
    },
    {
      name: 'ELA',
      category: 'Cold Chain Data Logger',
      image: '/assets/ELA_Blue_PUCK_T_EN12830.webp',
      description: 'EN12830 certified temperature logger designed for pharmaceutical, food, and medical supply chain monitoring. Features robust construction and compliance-grade accuracy for regulatory requirements.',
      detailedDescription: 'ELA sensors are professional-grade data loggers certified to EN12830 standards, making them essential for industries requiring regulatory compliance. These waterproof devices are built to withstand harsh environments and provide accurate temperature monitoring throughout the cold chain. Ideal for pharmaceutical transport, food logistics, and medical supply chains where compliance documentation is critical.',
      specs: [
        { label: 'Temperature Range', value: '-40°C to 70°C' },
        { label: 'Certification', value: 'EN12830 compliant' },
        { label: 'Waterproof Rating', value: 'IP67' },
        { label: 'Battery Life', value: '10 years' },
        { label: 'Connectivity', value: 'NFC & BLE' },
        { label: 'Data Points', value: '32,000+ readings' },
      ],
      features: ['EN12830 certified', 'IP67 waterproof', 'Dual connectivity (NFC/BLE)', 'Extended 10-year battery', 'Regulatory compliance', 'Tamper-proof design'],
    },
    {
      name: 'SIGMAWIT',
      category: 'Industrial IoT Sensor',
      image: '/assets/S6.webp',
      description: 'Advanced industrial-grade sensor with high-precision temperature and humidity monitoring. Designed for demanding environments requiring accurate, real-time data collection and analysis.',
      detailedDescription: 'SIGMAWIT sensors are professional IoT devices engineered for industrial applications where precision and reliability are paramount. These sensors provide comprehensive environmental monitoring with high accuracy measurements, making them suitable for manufacturing facilities, data centers, laboratories, and critical infrastructure monitoring. They offer seamless integration with cloud platforms and support advanced analytics.',
      specs: [
        { label: 'Temperature Range', value: '-40°C to 85°C' },
        { label: 'Humidity Range', value: '0-100% RH' },
        { label: 'Temperature Accuracy', value: '±0.3°C' },
        { label: 'Humidity Accuracy', value: '±3% RH' },
        { label: 'Battery Life', value: '2-3 years' },
        { label: 'Connectivity', value: 'BLE 5.0, WiFi, LoRaWAN' },
      ],
      features: ['High precision monitoring', 'Multi-connectivity options', 'Industrial-grade reliability', 'Cloud integration', 'Real-time analytics', 'Scalable deployment'],
    },
  ];

  const gateways = [
    {
      name: 'Gateway',
      category: 'Standard Gateway',
      image: '/assets/ca3fa4424957a6232681d8abcf39a7c3.webp',
      description: 'Central hub for connecting sensors to the cloud. Provides reliable wireless connectivity and real-time data synchronization for your sensor network.',
      detailedDescription: 'The Standard Gateway serves as the communication bridge between your sensors and the ChillVerify cloud platform. It collects data from multiple sensors via Bluetooth Low Energy (BLE) and transmits it to the cloud using WiFi or Ethernet connectivity. Perfect for small to medium deployments, this gateway supports up to 100 sensors and provides reliable data transmission with automatic reconnection capabilities. Easy to set up and configure, it requires minimal maintenance and provides continuous monitoring of your sensor network.',
      specs: [
        { label: 'Connectivity', value: 'WiFi & Ethernet' },
        { label: 'Sensor Capacity', value: '100+ sensors' },
        { label: 'Range', value: '100m indoor' },
        { label: 'BLE Support', value: 'BLE 5.0' },
        { label: 'Power', value: 'PoE or AC adapter' },
        { label: 'Cloud Sync', value: 'Real-time' },
        { label: 'Data Storage', value: 'Local buffer' },
      ],
      features: ['Easy setup and configuration', 'Dual connectivity options', 'Large sensor capacity', 'Reliable cloud sync', 'Automatic reconnection', 'Local data buffering', 'Web-based management'],
    },
    {
      name: 'Enterprise Gateway Pro',
      category: 'Enterprise Gateway',
      image: '/assets/ca3fa4424957a6232681d8abcf39a7c3.webp',
      description: 'Enterprise-grade gateway designed for large-scale deployments requiring high reliability, redundancy, and advanced connectivity options.',
      detailedDescription: 'The Enterprise Gateway Pro is built for mission-critical applications where uptime and reliability are paramount. It features redundant power supplies, 4G LTE backup connectivity, and supports up to 500 sensors simultaneously. This gateway is ideal for large warehouses, manufacturing facilities, and distributed operations where network reliability cannot be compromised. It includes advanced features like failover capabilities, remote management, full API access, and priority support. The gateway can operate independently with local data storage and automatically sync when connectivity is restored.',
      specs: [
        { label: 'Connectivity', value: 'WiFi, Ethernet, 4G LTE' },
        { label: 'Sensor Capacity', value: '500+ sensors' },
        { label: 'Range', value: '200m indoor' },
        { label: 'BLE Support', value: 'BLE 5.0' },
        { label: 'Power', value: 'Redundant power supplies' },
        { label: 'API Access', value: 'Full REST API' },
        { label: 'Data Storage', value: 'Extended local buffer' },
        { label: 'Failover', value: 'Automatic' },
      ],
      features: ['4G LTE backup connectivity', 'Redundant power supplies', 'Enterprise REST API', 'Priority support', 'Remote management', 'Failover capabilities', 'Extended sensor range', 'Advanced monitoring'],
    },
  ];

  const renderProductCard = (product) => (
    <Column lg={8} md={4} sm={4} key={product.name}>
      <Tile
        className="feature-card"
        style={{
          backgroundColor: bgSecondary,
          border: `1px solid ${borderColor}`,
          padding: '32px',
          marginBottom: '24px',
        }}
        data-testid={`sensor-${product.name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <Tag type="blue">{product.category}</Tag>
        </div>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <img
            src={product.image}
            alt={product.name}
            style={{ height: '150px', objectFit: 'contain' }}
          />
        </div>
        <h3 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, marginBottom: '12px' }}>
          {product.name}
        </h3>
        <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '12px', lineHeight: 1.6 }}>
          {product.description}
        </p>
        {product.detailedDescription && (
          <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '24px', lineHeight: 1.6 }}>
            {product.detailedDescription}
          </p>
        )}
        
        <h4 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>Specifications</h4>
        <div style={{ marginBottom: '24px' }}>
          {product.specs.map((spec, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${borderColor}` }}>
              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{spec.label}</span>
              <span style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 500 }}>{spec.value}</span>
            </div>
          ))}
        </div>
        
        <h4 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '12px' }}>Key Features</h4>
        <ul style={{ 
          listStyle: 'none', 
          padding: 0, 
          margin: '0 0 24px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}>
          {product.features.map((feature, idx) => (
            <li 
              key={idx} 
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                color: textSecondary,
                fontSize: '0.875rem'
              }}
            >
              <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
        
        <div style={{ display: 'flex', gap: '16px' }}>
          <Button kind="primary" onClick={() => navigate('/shop')} data-testid={`shop-${product.name.toLowerCase().replace(/\s+/g, '-')}`}>
            Shop Now
          </Button>
          <Button kind="tertiary" onClick={() => navigate('/contact')}>
            Learn More
          </Button>
        </div>
      </Tile>
    </Column>
  );

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="sensors-page">
      {/* Hero */}
      <section style={{ backgroundColor: '#0f62fe', padding: '80px 0' }}>
        <Grid>
          <Column lg={10} md={6} sm={4}>
            <div style={{ color: '#ffffff' }}>
              <Tag type="blue" style={{ marginBottom: '16px' }}>Hardware</Tag>
              <h1 style={{ fontSize: '3rem', fontWeight: 600, marginBottom: '24px' }}>
                Sensors & Integrations
              </h1>
              <p style={{ fontSize: '1.25rem', opacity: 0.9, marginBottom: '32px' }}>
                Professional-grade sensors and gateways for reliable environmental monitoring
              </p>
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Temperature size={20} /> Precision sensors
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Wifi size={20} /> Wireless connectivity
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <BatteryFull size={20} /> Long battery life
                </div>
              </div>
            </div>
          </Column>
        </Grid>
      </section>

      {/* Sensors Tabs */}
      <section style={{ padding: '48px 0', backgroundColor: bgPrimary }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            {/* Tab Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '0',
              marginBottom: '32px',
              borderBottom: `2px solid ${borderColor}`,
              paddingBottom: '0'
            }}>
              <button
                onClick={() => setActiveTab('sensors')}
                data-testid="tab-sensors"
                style={{ 
                  padding: '12px 24px',
                  fontSize: '0.875rem',
                  fontWeight: activeTab === 'sensors' ? 600 : 400,
                  color: activeTab === 'sensors' ? textPrimary : textSecondary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'sensors' ? `3px solid #0f62fe` : `3px solid transparent`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px',
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'sensors') {
                    e.target.style.color = textPrimary;
                    e.target.style.backgroundColor = bgSecondary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'sensors') {
                    e.target.style.color = textSecondary;
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Sensors & Loggers
              </button>
              <button
                onClick={() => setActiveTab('gateways')}
                data-testid="tab-gateways"
                style={{ 
                  padding: '12px 24px',
                  fontSize: '0.875rem',
                  fontWeight: activeTab === 'gateways' ? 600 : 400,
                  color: activeTab === 'gateways' ? textPrimary : textSecondary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'gateways' ? `3px solid #0f62fe` : `3px solid transparent`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px',
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'gateways') {
                    e.target.style.color = textPrimary;
                    e.target.style.backgroundColor = bgSecondary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'gateways') {
                    e.target.style.color = textSecondary;
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Gateways
              </button>
              <button
                onClick={() => setActiveTab('byo')}
                data-testid="tab-byo"
                style={{ 
                  padding: '12px 24px',
                  fontSize: '0.875rem',
                  fontWeight: activeTab === 'byo' ? 600 : 400,
                  color: activeTab === 'byo' ? textPrimary : textSecondary,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === 'byo' ? `3px solid #0f62fe` : `3px solid transparent`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  marginBottom: '-2px',
                  outline: 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== 'byo') {
                    e.target.style.color = textPrimary;
                    e.target.style.backgroundColor = bgSecondary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== 'byo') {
                    e.target.style.color = textSecondary;
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                Bring Your Own
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'sensors' && (
              <div style={{ paddingTop: '32px' }}>
                <Grid>
                  {sensors && sensors.length > 0 ? sensors.map(renderProductCard) : (
                    <Column lg={16} md={8} sm={4}>
                      <div style={{ padding: '32px', textAlign: 'center', color: textSecondary }}>
                        No sensors available
                      </div>
                    </Column>
                  )}
                  {/* Custom Integration Card */}
                  <Column lg={16} md={8} sm={4}>
                    <Tile
                      style={{
                        backgroundColor: '#6929c4',
                        padding: '48px',
                        color: '#ffffff',
                        marginTop: '24px',
                      }}
                      data-testid="custom-integration"
                    >
                      <WatsonHealthStackedScrolling_1 size={48} style={{ marginBottom: '24px' }} />
                      <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '16px' }}>
                        Custom Sensor Integration
                      </h3>
                      <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '24px', lineHeight: 1.6 }}>
                        Have your own sensors or need to integrate existing hardware? Our team can seamlessly connect your custom sensors into the ChillVerify platform for unified monitoring, analysis, and alerting. We support a wide range of protocols and can develop custom integrations tailored to your specific needs.
                      </p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0' }}>
                        {[
                          'Seamless connectivity to ChillVerify platform',
                          'Custom protocol support (MQTT, HTTP, Modbus, etc.)',
                          'Unified data analysis and visualization',
                          'Integrated alerting and notifications',
                          'No need to replace existing hardware',
                          'Dedicated integration support'
                        ].map((item, idx) => (
                          <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <Checkmark size={16} /> {item}
                          </li>
                        ))}
                      </ul>
                      <Button kind="secondary" onClick={() => navigate('/contact')} data-testid="custom-integration-contact">
                        Request Custom Integration <ArrowRight style={{ marginLeft: '8px' }} />
                      </Button>
                    </Tile>
                  </Column>
                </Grid>
              </div>
            )}

            {activeTab === 'gateways' && (
              <div style={{ paddingTop: '32px' }}>
                <Grid>
                  {gateways.map(renderProductCard)}
                </Grid>
              </div>
            )}

            {activeTab === 'byo' && (
              <div style={{ paddingTop: '32px' }}>
                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <Tile
                      style={{
                        backgroundColor: '#6929c4',
                        padding: '48px',
                        color: '#ffffff',
                      }}
                      data-testid="byo-section"
                    >
                      <WatsonHealthStackedScrolling_1 size={48} style={{ marginBottom: '24px' }} />
                      <h3 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '16px' }}>
                        Bring Your Own (BYO)
                      </h3>
                      <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '24px' }}>
                        Already have sensors or gateways? Tell us your brand and we'll customize integration for your existing hardware - at no additional cost.
                      </p>
                      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 32px 0' }}>
                        {['Custom integration', 'No replacement needed', 'Free setup', 'Full platform access'].map((item, idx) => (
                          <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                            <Checkmark size={16} /> {item}
                          </li>
                        ))}
                      </ul>
                      <Button kind="secondary" onClick={() => navigate('/contact')} data-testid="byo-contact">
                        Contact Us <ArrowRight style={{ marginLeft: '8px' }} />
                      </Button>
                    </Tile>
                  </Column>
                  <Column lg={8} md={4} sm={4}>
                    <Tile
                      style={{
                        backgroundColor: bgSecondary,
                        border: `1px solid ${borderColor}`,
                        padding: '48px',
                      }}
                    >
                      <Certificate size={48} style={{ color: '#0f62fe', marginBottom: '24px' }} />
                      <h3 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, marginBottom: '16px' }}>
                        Supported Brands
                      </h3>
                      <p style={{ color: textSecondary, marginBottom: '24px' }}>
                        We support a wide range of sensor and gateway manufacturers:
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {['KKM', 'ELA', 'SIGMAWIT', 'Teltonika', 'Ruuvi', 'Moko', 'Others'].map((brand, idx) => {
                          const comingSoonBrands = ['Teltonika', 'Ruuvi', 'Moko'];
                          const isComingSoon = comingSoonBrands.includes(brand);
                          return (
                            <Tag 
                              key={idx} 
                              type="outline"
                              style={{
                                opacity: isComingSoon ? 0.5 : 1,
                                color: isComingSoon ? textSecondary : undefined,
                                position: 'relative'
                              }}
                            >
                              {brand}
                              {isComingSoon && (
                                <span style={{ 
                                  fontSize: '0.625rem', 
                                  marginLeft: '4px',
                                  color: textSecondary,
                                  fontStyle: 'italic'
                                }}>
                                  (Coming Soon)
                                </span>
                              )}
                            </Tag>
                          );
                        })}
                      </div>
                    </Tile>
                  </Column>
                </Grid>
              </div>
            )}
          </Column>
        </Grid>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: bgSecondary, padding: '80px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ color: textPrimary, fontSize: '2rem', fontWeight: 600, marginBottom: '16px' }}>
                Ready to Start Monitoring?
              </h2>
              <p style={{ color: textSecondary, marginBottom: '32px' }}>
                Browse our shop or contact us for custom solutions
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Button kind="primary" size="lg" onClick={() => navigate('/shop')} data-testid="sensors-shop-cta">
                  Visit Shop
                </Button>
                <Button kind="tertiary" size="lg" onClick={() => navigate('/contact')} data-testid="sensors-contact-cta">
                  Contact Sales
                </Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default SensorsPage;
